import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  HiOutlineQuestionMarkCircle,
  HiOutlinePlayCircle,
  HiOutlineNewspaper,
  HiPlay,
  HiArrowRight,
} from "react-icons/hi2";
import Accordions from "../mui/Accordian";
import LoadingSpinner from "./loadingAnimation";
import { sanityClient, urlFor } from "../../sanityClient";
import VideoPlayerModal from "../modals/VideoPlayerModal";

const TAB_KEYS = ["faq", "videos", "blog"];
const TAB_INDEX_BY_KEY = { faq: 0, videos: 1, blog: 2 };

const TABS = [
  {
    key: "faq",
    label: "FAQ",
    shortLabel: "FAQ",
    icon: HiOutlineQuestionMarkCircle,
    title: "Frequently Asked Questions",
    description: "Quick answers to common questions about Mockee.",
  },
  {
    key: "videos",
    label: "Video Tutorials",
    shortLabel: "Videos",
    icon: HiOutlinePlayCircle,
    title: "Video Tutorials",
    description: "Watch step-by-step guides to get the most out of Mockee.",
  },
  {
    key: "blog",
    label: "Blog",
    shortLabel: "Blog",
    icon: HiOutlineNewspaper,
    title: "Blog",
    description: "Tips, updates, and inspiration for growing your business.",
  },
];

const getTabIndexFromParams = (searchParams) => {
  const tab = searchParams.get("tab");
  return TAB_INDEX_BY_KEY[tab] ?? 0;
};

const getMediaUrl = (field, width = 800, height = 450) => {
  if (!field) return null;
  try {
    if (Array.isArray(field)) {
      const block = field.find((item) => item._type === "image");
      if (block) {
        return urlFor(block).width(width).height(height).fit("crop").url();
      }
    }
    if (field?.asset?._ref || field?.asset?._id) {
      return urlFor(field).width(width).height(height).fit("crop").url();
    }
  } catch {
    return null;
  }
  return null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SectionLoader = () => (
  <div className="learn-page__loader">
    <LoadingSpinner />
  </div>
);

const EmptyState = ({ message }) => (
  <div className="learn-page__empty">
    <p>{message}</p>
  </div>
);

const LearnVideoCard = ({ video, onPlay }) => {
  const thumbnailUrl = getMediaUrl(video?.thumbnail, 640, 360);

  return (
    <button
      type="button"
      className="learn-page__video-card"
      onClick={() => onPlay(video?.videoUrl)}
    >
      <div className="learn-page__video-thumb">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={video?.title || "Video thumbnail"} />
        ) : (
          <div className="learn-page__video-thumb-placeholder" />
        )}
        <span className="learn-page__video-play" aria-hidden="true">
          <HiPlay size={22} />
        </span>
      </div>
      <div className="learn-page__video-body">
        <h3 className="learn-page__card-title">{video?.title}</h3>
        {video?.short_description && (
          <p className="learn-page__card-desc">{video.short_description}</p>
        )}
      </div>
    </button>
  );
};

const LearnBlogCard = ({ blog }) => {
  const imageUrl = getMediaUrl(blog?.mainImage, 640, 360);

  return (
    <Link to={`/blog/${blog?.slug}`} className="learn-page__blog-card">
      <div className="learn-page__blog-thumb">
        {imageUrl ? (
          <img src={imageUrl} alt={blog?.title || "Blog cover"} />
        ) : (
          <div className="learn-page__blog-thumb-placeholder">
            <HiOutlineNewspaper size={32} />
          </div>
        )}
      </div>
      <div className="learn-page__blog-body">
        {blog?.publishedAt && (
          <time className="learn-page__blog-date" dateTime={blog.publishedAt}>
            {formatDate(blog.publishedAt)}
          </time>
        )}
        <h3 className="learn-page__card-title">{blog?.title}</h3>
        {blog?.short_description && (
          <p className="learn-page__card-desc">{blog.short_description}</p>
        )}
        <span className="learn-page__read-more">
          Read more
          <HiArrowRight size={16} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
};

const Learn = () => {
  const theme = useTheme();
  const isCompactNav = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(() => getTabIndexFromParams(searchParams));
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);

  useEffect(() => {
    setValue(getTabIndexFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    getPosts();
    getVideos();
    getFaqs();
  }, []);

  const handleTabChange = (newValue) => {
    setValue(newValue);
    setSearchParams({ tab: TAB_KEYS[newValue] }, { replace: true });
  };

  const handlePlayVideo = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setShowVideoModal(true);
  };

  const getVideos = async () => {
    setVideosLoading(true);
    try {
      const query = `*[_type == "video"] | order(sort asc) {
    _id,
    title,
    videoUrl,
    thumbnail,
    short_description
  }`;
      const fetchedVideos = await sanityClient.fetch(query);
      setVideos(fetchedVideos);
    } catch (error) {
      console.error("Error fetching videos:", error);
      setVideos([]);
    } finally {
      setVideosLoading(false);
    }
  };

  const getFaqs = async () => {
    setFaqsLoading(true);
    try {
      const query = `*[_type == "faq"] | order(sort asc) {
    _id,
    title,
    content,
  }`;
      const faq = await sanityClient.fetch(query);
      setFaqs(faq);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      setFaqs([]);
    } finally {
      setFaqsLoading(false);
    }
  };

  const getPosts = async () => {
    setBlogsLoading(true);
    try {
      const query = `*[_type == "post"] | order(sort asc) {
    _id,
    title,
    "slug": slug.current,
    mainImage,
    publishedAt,
    short_description
  }`;
      const posts = await sanityClient.fetch(query);
      setBlogs(posts);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      setBlogs([]);
    } finally {
      setBlogsLoading(false);
    }
  };

  const activeTab = TABS[value];
  const tabCounts = [faqs.length, videos.length, blogs.length];

  return (
    <div className="learn-page">
      <section className="learn-page__hero-section px-3">
        <div className="container-lg learn-page__hero px-0">
          <div className="row g-0 align-items-center">
            <div className="col-lg-6 offset-lg-1 px-3 px-lg-4">
              <div className="learn-page__hero-copy py-4 py-md-5">
                <span className="learn-page__eyebrow">Resources</span>
                <h1 className="learn-page__title">Learn</h1>
                <p className="learn-page__lead">
                  Learn how to use Mockee to grow your online business. Watch
                  our video tutorials, read our FAQs, or browse our blog.
                </p>
                <div className="learn-page__hero-stats">
                  {TABS.map((tab, index) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`learn-page__stat${
                        value === index ? " learn-page__stat--active" : ""
                      }`}
                      onClick={() => handleTabChange(index)}
                    >
                      <span className="learn-page__stat-value">
                        {tabCounts[index]}
                      </span>
                      <span className="learn-page__stat-label">
                        {tab.shortLabel}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-5 px-0 d-none d-md-block">
              <div className="learn-page__hero-art">
                <img
                  src="/assets/images/learn.svg"
                  alt=""
                  className="learn-page__hero-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="learn-page__main-section px-3 pb-5">
        <div className="container-lg">
          <div className="learn-page__layout">
            <nav
              className="learn-page__nav"
              aria-label="Learn sections"
            >
              {TABS.map((tab, index) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    className={`learn-page__nav-item${
                      value === index ? " learn-page__nav-item--active" : ""
                    }`}
                    onClick={() => handleTabChange(index)}
                    aria-current={value === index ? "page" : undefined}
                    aria-label={`${tab.label} (${tabCounts[index]})`}
                  >
                    <Icon className="learn-page__nav-icon" aria-hidden="true" />
                    <span className="learn-page__nav-label">
                      {isCompactNav ? tab.shortLabel : tab.label}
                    </span>
                    <span className="learn-page__nav-count">
                      {tabCounts[index]}
                    </span>
                  </button>
                );
              })}
            </nav>

            <div className="learn-page__content">
              <header className="learn-page__panel-header">
                <h2 className="learn-page__panel-title">{activeTab.title}</h2>
                <p className="learn-page__panel-desc">
                  {activeTab.description}
                </p>
              </header>

              {value === 0 && (
                <div className="learn-page__panel">
                  {faqsLoading ? (
                    <SectionLoader />
                  ) : faqs.length > 0 ? (
                    <div className="learn-page__faq">
                      <Accordions faqs={faqs} />
                    </div>
                  ) : (
                    <EmptyState message="No FAQs available yet." />
                  )}
                </div>
              )}

              {value === 1 && (
                <div className="learn-page__panel">
                  {videosLoading ? (
                    <SectionLoader />
                  ) : videos.length > 0 ? (
                    <div className="learn-page__video-grid">
                      {videos.map((video) => (
                        <LearnVideoCard
                          key={video._id}
                          video={video}
                          onPlay={handlePlayVideo}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No video tutorials available yet." />
                  )}
                </div>
              )}

              {value === 2 && (
                <div className="learn-page__panel">
                  {blogsLoading ? (
                    <SectionLoader />
                  ) : blogs.length > 0 ? (
                    <div className="learn-page__blog-grid">
                      {blogs.map((blog) => (
                        <LearnBlogCard key={blog._id} blog={blog} />
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No blog posts available yet." />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {currentVideoUrl && (
        <VideoPlayerModal
          open={showVideoModal}
          onClose={() => {
            setShowVideoModal(false);
            setCurrentVideoUrl(null);
          }}
          videoUrl={currentVideoUrl}
        />
      )}
    </div>
  );
};

export default Learn;
